import React, { useCallback } from "react";

import {
  emitUpsellModalAction,
  OnePassUpsellModalLabel,
} from "@catchoftheday/analytics";
import {
  Box,
  Flex,
  Heading,
  IconBellRinging,
  IconMemberDiscount,
  IconOnePassDelivery,
  IconOnePassTags,
  Link,
  LinkButton,
  Span,
  Text,
} from "@catchoftheday/cg-components";
import { URL_LOGIN } from "@catchoftheday/common-vendors";
import {
  CTAButton as OnePassCTAButton,
  generateOnePassUpsellUrl,
  triggerOnePassLinkingModal,
  URL_ONEPASS_LANDING,
} from "@catchoftheday/onepass";
import { formatPrice } from "@catchoftheday/utilities";

import { OnePassHeader } from "@header/components/OnePassHeader";

import { BenefitBox, BenefitBoxProps } from "./BenefitBox";

export const BENEFITS: BenefitBoxProps[] = [
  {
    icon: (
      <IconOnePassDelivery
        title="Free delivery with OnePass"
        height="26px"
        width="auto"
      />
    ),
    title: "Free Delivery",
    subtitle:
      "On eligible items or orders at Catch, Kmart, Target and Bunnings Warehouse",
  },
  {
    icon: (
      <IconMemberDiscount
        title="OnePass member only pricing"
        height="36px"
        width="auto"
      />
    ),
    title: "Member only pricing",
    subtitle: "Member-only prices on thousands of eligible items at Catch",
  },
  {
    icon: (
      <IconOnePassTags
        title="OnePass exclusive deals"
        height="34px"
        width="auto"
      />
    ),
    title: "Exclusive deals",
    subtitle: "Access to exclusive weekly deals at Catch",
  },
  {
    icon: (
      <IconBellRinging
        title="OnePass early alerts"
        height="30px"
        width="auto"
      />
    ),
    title: "Early alerts",
    subtitle: "Personalised offers from participating retail brands",
  },
];

export type PopupContentProps = {
  hideContent?: boolean;
  showFreeTrial?: boolean;
  showLogIn?: boolean;
  showAlreadyAMember?: boolean;
  monthlyPrice: number;
  yearlyPrice: number;
};

export const PopupContent: React.FC<PopupContentProps> = ({
  hideContent,
  showFreeTrial,
  showLogIn,
  showAlreadyAMember,
  monthlyPrice,
  yearlyPrice,
}) => {
  const trackClicks = (label: OnePassUpsellModalLabel) => {
    emitUpsellModalAction({
      action: "Upsell Modal Header",
      label,
    });
  };

  const onLinkButtonClick = useCallback(() => {
    trackClicks("link");
    triggerOnePassLinkingModal({
      originalUrl: window.location.pathname,
    });
  }, []);

  if (hideContent) {
    return null;
  }
  return (
    <Flex
      width="400px"
      flexDirection="column"
      color="typography.text"
      boxShadow="0 2px 4px 0 rgba(0,0,0,0.50)"
      bg="white"
      borderRadius="16px"
      overflow="hidden"
      fontFamily="onePass"
    >
      <OnePassHeader>
        <Heading.h3
          fontFamily="inherit"
          fontWeight="bold"
          m="0px"
          mb="10px"
          color="inherit"
        >
          Your pass to a world of benefits
        </Heading.h3>
        <Text m="0" textAlign="center" fontSize="xs" fontWeight="medium">
          {showFreeTrial
            ? `After your free trial, OnePass is only ${formatPrice(
                monthlyPrice,
                false
              )}/month or
              ${formatPrice(yearlyPrice, false)}/year. Cancel anytime.`
            : `OnePass is only ${formatPrice(
                monthlyPrice,
                false
              )}/month or ${formatPrice(
                yearlyPrice,
                false
              )}/year. Cancel anytime.`}
        </Text>
      </OnePassHeader>
      <Box py="20px" px="14px">
        <Flex
          mb="12px"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <LinkButton
            href={generateOnePassUpsellUrl({
              url: window.location.pathname,
              cta: showFreeTrial ? "start-free-trial" : "join",
            })}
            fontSize="16px"
            fontWeight="semibold"
            sizeVariant="sm"
            p="12px 24px"
            variant="onePassPrimary"
            width="auto"
            onClick={() => trackClicks(showFreeTrial ? "freeTrial" : "join")}
          >
            {showFreeTrial ? "Start free 30 Day Trial" : "Join OnePass"}
          </LinkButton>
          {showAlreadyAMember && (
            <Flex mt="16px" color="onePass.primaryDarker">
              <Span fontSize="12px" fontWeight="medium" mr="2px">
                Already a member?
              </Span>
              {showLogIn ? (
                <Link
                  href={URL_LOGIN}
                  pl="2px"
                  fontSize="12px"
                  fontWeight="bold"
                  color="onePass.primary"
                  textDecoration="underline"
                  onClick={() => trackClicks("login")}
                >
                  Log in
                </Link>
              ) : (
                <OnePassCTAButton pl="2px" onClick={onLinkButtonClick}>
                  Link OnePass with Catch
                </OnePassCTAButton>
              )}
            </Flex>
          )}
        </Flex>
        <Flex flexWrap="wrap">
          {BENEFITS.map((benefit, idx) => (
            <BenefitBox
              key={`onepass-benefit-${idx}`}
              icon={benefit.icon}
              title={benefit.title}
              subtitle={benefit.subtitle}
            />
          ))}
        </Flex>
        <Flex mt="10px" alignItems="center" justifyContent="center">
          <Link
            fontSize="xs"
            href={URL_ONEPASS_LANDING}
            fontWeight="bold"
            color="onePass.primary"
            textDecoration="underline"
            target="_blank"
            onClick={() => trackClicks("learnMore")}
          >
            Learn more
          </Link>
        </Flex>
      </Box>
    </Flex>
  );
};
